import React, { useState } from "react";
import { jarvisChat } from "@/lib/api";

/**
 * CommandRail — Left-edge clickable sidebar of every voice command, used as a
 * stand-in while the owner waits for a USB microphone to arrive. Clicking a
 * command sends it through the same /api/jarvis/chat endpoint that the voice
 * pipeline uses, so the brain processes it identically — same panels pop up,
 * same lightning, same TTS reply.
 *
 * The `rating` (1-5 stars) is a quick personal-relevance hint so the most
 * useful daily-driver commands surface at the top of each section.
 */
const CATEGORIES = [
  { title: "👁 EYES — Vision (NEW)", items: [
    { t: "Open EYES panel",        say: "open my eyes panel",                                              rating: 5 },
    { t: "What do you see?",       say: "look at this and tell me what you see",                           rating: 5 },
    { t: "Read this label",        say: "look at this label and read it out",                              rating: 5 },
    { t: "What should I do next?", say: "look at this and tell me what I should do next",                  rating: 5 },
    { t: "Keep watching mode",     say: "turn on keep watching mode",                                       rating: 4 },
    { t: "Stop watching",          say: "turn off keep watching",                                           rating: 3 },
    { t: "Use webcam mode",        say: "switch eyes to webcam mode",                                       rating: 3 },
    { t: "Use ESP32 cam",          say: "switch eyes to my ESP32 camera",                                   rating: 4 },
  ]},
  { title: "⏰ Alarms & Wake-up (NEW)", items: [
    { t: "Set alarm for 7am",         say: "set an alarm for 7am",                                          rating: 5 },
    { t: "Wake me up at 6:30",        say: "wake me up at 6:30 tomorrow",                                   rating: 5 },
    { t: "Daily alarm 7am",           say: "set a daily alarm for 7am to wake up",                          rating: 5 },
    { t: "Weekday alarm",             say: "set an alarm every weekday at 7am",                             rating: 4 },
    { t: "Weekend alarm",             say: "set an alarm every weekend at 9am",                             rating: 3 },
    { t: "List my alarms",            say: "what alarms have I got",                                        rating: 4 },
    { t: "Delete the 7am alarm",      say: "delete my 7am alarm",                                           rating: 4 },
    { t: "Open the alarms widget",    say: "open the alarms widget",                                        rating: 4 },
  ]},
  { title: "🔁 Recurring Reminders (NEW)", items: [
    { t: "Daily exercise + water",    say: "remind me every morning at 7am to do 5 minute exercise and drink a pint of water", rating: 5 },
    { t: "Daily 5pm walk",            say: "remind me every day at 5pm to go for a walk",                   rating: 4 },
    { t: "Weekday morning check",     say: "remind me every weekday at 8am to check the markets",           rating: 4 },
    { t: "Weekend lie-in nudge",      say: "remind me every weekend at 9am to relax",                       rating: 2 },
    { t: "One-off reminder",          say: "remind me at 6pm to call mum",                                  rating: 4 },
    { t: "List my reminders",         say: "what reminders have I got",                                     rating: 4 },
    { t: "Delete a reminder",         say: "delete my 7am reminder",                                        rating: 3 },
  ]},
  { title: "🔌 Smart Plugs (Shelly) (NEW)", items: [
    { t: "Turn on living room plug",  say: "turn on the living room plug",                                  rating: 5 },
    { t: "Turn off living room TV",   say: "turn off the living room plug",                                 rating: 5 },
    { t: "Turn on bedroom plug",      say: "good morning Raw Company",                                      rating: 5 },
    { t: "Turn off bedroom + PC",     say: "good night Raw Company",                                        rating: 5 },
    { t: "Register a new plug",       say: "I want to add a new shelly plug — its device id is xxxxx",      rating: 3 },
    { t: "List my plugs",             say: "what plugs have I got registered",                              rating: 3 },
  ]},
  { title: "💬 Talk to specific agents (NEW)", items: [
    { t: "Ask Tom (Dev)",             say: "ask Tom to add a swarmchat widget",                              rating: 5 },
    { t: "Ask Sarah (Support)",       say: "tell Sarah to draft a refund email",                             rating: 5 },
    { t: "Ask Eva (Design)",          say: "ask Eva to redesign the brief tiles",                            rating: 4 },
    { t: "Ask Scout (Research)",      say: "get Scout to research the gold price for me",                    rating: 4 },
    { t: "Ask Dobby (Ads)",           say: "tell Dobby to schedule a TikTok for tomorrow",                   rating: 4 },
    { t: "Ask ALL agents (SwarmChat)", say: "ask all my agents what I should focus on this week",            rating: 5 },
    { t: "Open SwarmChat panel",      say: "open the swarm chat",                                            rating: 5 },
    { t: "Clear SwarmChat history",   say: "clear my swarmchat history",                                     rating: 3 },
  ]},
  { title: "💸 Add money / log finance (NEW)", items: [
    { t: "Add £500 Emergent revenue", say: "add 500 pounds to my Emergent revenue",                          rating: 5 },
    { t: "Top-up Emergent £20",       say: "I just topped up Emergent for 20 quid",                          rating: 4 },
    { t: "Add £200 clothing sales",   say: "add 200 pounds to revenue for clothing",                         rating: 4 },
    { t: "Log Stripe fee £45",        say: "log 45 pounds spend on Stripe fees",                             rating: 3 },
    { t: "Add £1500 Capital P&L",     say: "add 1500 pounds to my Capital trading profit",                   rating: 4 },
  ]},
  { title: "🎬 Morning Brief (UPDATED)", items: [
    { t: "Give me my morning brief",  say: "give me my morning brief",                                       rating: 5 },
    { t: "Set my city",               say: "set my city to Sedbergh",                                        rating: 3 },
    { t: "What's the weather",        say: "what's the weather like today",                                  rating: 4 },
    { t: "BBC top headlines",         say: "read me the BBC top headlines",                                  rating: 4 },
    { t: "Today's calendar",          say: "what's on my calendar today",                                    rating: 5 },
    { t: "Overnight report",          say: "what did you do overnight",                                       rating: 4 },
  ]},
  { title: "🤖 Agent Studio (NEW)", items: [
    { t: "Open Agent Studio",  say: "open agent studio",                                        rating: 5 },
    { t: "Run a workflow",     say: "open agent studio on the workflows tab",                   rating: 5 },
    { t: "Ask Tom (Dev)",      say: "open agent studio and run Tom",                            rating: 4 },
    { t: "Ask Sarah (Support)",say: "open agent studio and run Sarah",                          rating: 4 },
    { t: "Ask Eva (Design)",   say: "open agent studio and run Eva",                            rating: 4 },
    { t: "Ask Scout (Research)",say: "open agent studio and run Scout",                          rating: 4 },
    { t: "Ask Dobby (Tasks)",  say: "open agent studio and run Dobby",                          rating: 4 },
    { t: "Build a new agent",  say: "open agent studio so I can build a new agent",             rating: 4 },
    { t: "How agents work (NEW)", say: "show me how I've made an agent and how the agent works", rating: 5 },
    { t: "🛠 Tom's Workshop",      say: "open Tom's workshop", rating: 5 },
  ]},
  { title: "📣 Social & Comms (NEW)", items: [
    { t: "Post to Instagram",  say: "open the social composer for instagram",                   rating: 5 },
    { t: "Post to Facebook",   say: "open the social composer for facebook",                    rating: 4 },
    { t: "Post to TikTok",     say: "open the social composer for tiktok",                      rating: 4 },
    { t: "Send email (Resend)",say: "send an email via resend",                                 rating: 4 },
    { t: "Who's at the door?", say: "show me the doorbell",                                     rating: 5 },
    { t: "My YouTube stats",   say: "show me my recent YouTube videos",                         rating: 3 },
    { t: "Open Ring app",      say: "open my ring doorbell dashboard",                          rating: 3 },
    { t: "Open Gmail",         say: "open gmail",                                               rating: 4 },
    { t: "Open Calendar",      say: "open google calendar",                                      rating: 4 },
  ]},
  { title: "⚙️ Settings & Diagnostics", items: [
    { t: "Open settings", say: "open settings", rating: 5 },
    { t: "Paste my API keys", say: "open settings on the keys tab", rating: 5 },
    { t: "Diagnostics", say: "run diagnostics on all my integrations", rating: 5 },
    { t: "Upload wake song", say: "let me upload my wake song", rating: 4 },
    { t: "Add external app key", say: "open settings to add an external app", rating: 4 },
    { t: "Change my name", say: "open settings so I can change your name", rating: 3 },
    { t: "Speak slower", say: "speak a bit slower from now on", rating: 4 },
    { t: "Wipe demo data", say: "wipe the demo data", rating: 2 },
  ]},
  { title: "💬 Chat & greetings", items: [
    { t: "Raw Company", say: "Raw Company", rating: 3 },
    { t: "Up late, sir?", say: "are you still online?", rating: 2 },
    { t: "How are you?", say: "how's your day going?", rating: 2 },
    { t: "Who are you?", say: "who are you, tell me about yourself", rating: 3 },
    { t: "How do you work?", say: "tell me everything about how you were built", rating: 4 },
    { t: "Tell me a story", say: "tell me a quick story", rating: 3 },
    { t: "Teach me something", say: "teach me something new", rating: 3 },
  ]},
  { title: "📈 Capital.com trading", items: [
    { t: "Capital P&L", say: "show me my live capital P&L", rating: 5 },
    { t: "Open trades", say: "what trades have I got open right now", rating: 5 },
    { t: "Close all trades", say: "close all my trades", rating: 4 },
    { t: "Buy gold £100", say: "place a 100 pound buy on gold", rating: 4 },
    { t: "Sell gold £200", say: "place a 200 pound sell on gold", rating: 4 },
    { t: "Sell BTC £500", say: "place a 500 pound sell on Bitcoin", rating: 3 },
    { t: "Break-even on gold", say: "set break-even on gold", rating: 4 },
    { t: "Switch to LIVE", say: "switch to live mode", rating: 4 },
    { t: "Switch to DEMO", say: "switch to demo mode", rating: 4 },
    { t: "Open my trader app", say: "open my trading app on gold live signals", rating: 4 },
    { t: "Withdraw £50 → bank", say: "transfer 50 quid out of my capital account to my bank", rating: 3 },
  ]},
  { title: "🌐 RAW Company Hub", items: [
    { t: "Open the hub", say: "open my RAW Company hub dashboard", rating: 5 },
    { t: "Live gold signal", say: "what's my live gold signal saying", rating: 4 },
    { t: "Family feed", say: "show me the family feed across all my apps", rating: 4 },
    { t: "Stripe balance", say: "what's in my Stripe right now", rating: 4 },
    { t: "Sync SnapDog", say: "sync the snapdog stats", rating: 3 },
    { t: "Sync clothing", say: "sync the clothing stats", rating: 3 },
  ]},
  { title: "💰 Money & finance", items: [
    { t: "Morning brief", say: "give me my morning brief", rating: 5 },
    { t: "Overnight report", say: "what did you do overnight", rating: 5 },
    { t: "Revenue this week", say: "what's my revenue this week", rating: 5 },
    { t: "Open calendar", say: "open my calendar with the pound figures on each day", rating: 5 },
    { t: "Year heatmap", say: "show me the year heatmap", rating: 4 },
    { t: "Last month rundown", say: "give me last month's rundown", rating: 5 },
    { t: "This month so far", say: "how am I doing this month so far", rating: 4 },
    { t: "Daily summary", say: "give me my daily summary", rating: 4 },
    { t: "Log Emergent £50", say: "log an Emergent top-up of 50 quid", rating: 3 },
    { t: "Add £200 revenue (clothing)", say: "add 200 pounds to revenue for clothing", rating: 3 },
  ]},
  { title: "🖥️ My apps & ecosystem", items: [
    { t: "Pull up all dashboards", say: "pull up all my dashboards in a multi screen view", rating: 5 },
    { t: "Command center", say: "show me the command center", rating: 5 },
    { t: "Open SnapDog", say: "open SnapDog", rating: 3 },
    { t: "Open Clothing app", say: "pull up my Clothing app", rating: 3 },
    { t: "Open RAW Trading", say: "open my trader app", rating: 4 },
    { t: "Read SnapDog messages", say: "read my messages from SnapDog", rating: 3 },
    { t: "Auto-fix everything", say: "auto-fix everything you can across my apps", rating: 4 },
    { t: "Register a new app", say: "register a new external app", rating: 3 },
  ]},
  { title: "📝 Notes, reminders, watches", items: [
    { t: "Add a note", say: "make a note: ", rating: 4 },
    { t: "List notes", say: "list my recent notes", rating: 4 },
    { t: "Add a reminder", say: "remind me at 8pm to ", rating: 4 },
    { t: "List reminders", say: "what reminders have I got", rating: 4 },
    { t: "List watches", say: "list my watches", rating: 3 },
    { t: "Add a watch", say: "watch Bitcoin and alert me at 70000", rating: 3 },
  ]},
  { title: "✈️ Travel & life", items: [
    { t: "Flights LHR → JFK", say: "find me flights from London Heathrow to New York JFK next month", rating: 4 },
    { t: "Walk from A to B", say: "how do I walk from London Bridge to Tower Bridge", rating: 3 },
    { t: "Drive directions", say: "give me driving directions from Manchester to Leeds", rating: 3 },
    { t: "Text me", say: "send me a text saying 'reminder: pick up dry cleaning'", rating: 3 },
    { t: "Recipe lookup", say: "find me a chicken curry recipe", rating: 3 },
  ]},
  { title: "🌍 Real-time info", items: [
    { t: "Top news", say: "read me the BBC top headlines", rating: 4 },
    { t: "Live BBC", say: "put the live BBC news on", rating: 3 },
    { t: "Weather here", say: "what's the weather like", rating: 4 },
    { t: "Set my city", say: "set my city to ", rating: 3 },
    { t: "Web search", say: "search the web for ", rating: 3 },
    { t: "eBay search", say: "find me on eBay ", rating: 3 },
    { t: "Open YouTube", say: "put a murder documentary on YouTube", rating: 3 },
    { t: "Maths", say: "what's 87 plus 64", rating: 3 },
  ]},
  { title: "🎨 Look & feel", items: [
    { t: "BG: midnight blue", say: "change my background to midnight blue", rating: 3 },
    { t: "BG: dark purple", say: "change my background to dark purple", rating: 2 },
    { t: "BG: deep green", say: "change my background to deep green", rating: 2 },
    { t: "BG: pure black", say: "change my background to pure black", rating: 2 },
    { t: "BG: GALAXY ✨ (NEW)", say: "change my background to galaxy", rating: 5 },
    { t: "BG: WAVEFORM (NEW)", say: "change my background to waveform", rating: 5 },
    { t: "BG: NEURAL BRAIN (NEW)", say: "change my background to neural brain", rating: 5 },
    { t: "BG: 🧠 Z.E.R.O. 3D BRAIN", say: "switch to the neuralink brain background", rating: 5 },
    { t: "BG: DATA GRID (NEW)", say: "change my background to data grid", rating: 5 },
    { t: "BG: gold grid (default)", say: "change my background to tinted black", rating: 3 },
    { t: "Orb: constellation", say: "change the orb to constellation", rating: 3 },
    { t: "Orb: nebula", say: "change the orb to nebula", rating: 3 },
    { t: "Orb: matrix", say: "change the orb to the matrix style", rating: 4 },
    { t: "Orb: plasma", say: "change the orb to the plasma style", rating: 4 },
    { t: "Lightning: gold", say: "change the lightning to gold", rating: 3 },
    { t: "Lightning: blue", say: "change the lightning to electric blue", rating: 3 },
    { t: "Lightning: off", say: "turn off the lightning", rating: 2 },
    { t: "Add clock (top-right)", say: "add a clock to the top right of my background", rating: 3 },
    { t: "Add illuminati eye", say: "add the all-seeing eye to the top centre", rating: 2 },
    { t: "Add BTC ticker", say: "add a Bitcoin price ticker to the top left", rating: 3 },
    { t: "Add moon phase", say: "add a moon phase widget", rating: 2 },
    { t: "Clear all widgets", say: "clear the background", rating: 3 },
  ]},
  { title: "🌐 Open my apps (site or dashboard)", items: [
    { t: "SnapDog (site)",            say: "open snapdog", rating: 5 },
    { t: "SnapDog (dashboard)",       say: "open my snapdog dashboard", rating: 5 },
    { t: "Pin SnapDog as widget",     say: "pin snapdog as a widget", rating: 4 },
    { t: "Raw Company (site)",        say: "open raw company", rating: 5 },
    { t: "Raw Company (hub)",         say: "open my raw company hub", rating: 5 },
    { t: "Pin Raw Company widget",    say: "pin raw company as a widget", rating: 4 },
    { t: "Clothing (site)",           say: "open my clothing app", rating: 4 },
    { t: "Clothing (dashboard)",      say: "open my clothing dashboard", rating: 4 },
    { t: "Pin Clothing widget",       say: "pin clothing as a widget", rating: 3 },
    { t: "Trader (site)",             say: "open my trader app", rating: 4 },
    { t: "Trader (dashboard)",        say: "open my trader dashboard", rating: 4 },
    { t: "Pin Trader widget",         say: "pin trader as a widget", rating: 3 },
    { t: "Racing Form Guide (site)",  say: "open my racing form guide", rating: 5 },
    { t: "Racing FG (dashboard)",     say: "open my racing form guide dashboard", rating: 4 },
    { t: "Pin Racing widget",         say: "pin racing form guide as a widget", rating: 4 },
    { t: "Raw Mixes (site)",          say: "open my raw mixes", rating: 5 },
    { t: "Raw Mixes (dashboard)",     say: "open my raw mixes dashboard", rating: 4 },
    { t: "Pin Raw Mixes widget",      say: "pin raw mixes as a widget", rating: 4 },
    { t: "RC Trading (site)",         say: "open my raw company trading platform", rating: 5 },
    { t: "RC Trading (dashboard)",    say: "open my raw company trading dashboard", rating: 4 },
    { t: "Pin RC Trading widget",     say: "pin my raw company trading platform as a widget", rating: 4 },
  ]},
  { title: "🎛 Widgets on home screen", items: [
    { t: "Live Users (top-right)",   say: "add a live users widget to the top right", rating: 5 },
    { t: "Weather large (bottom-right)", say: "add the weather widget large to the bottom right", rating: 5 },
    { t: "Clock (top-left)",         say: "add a clock to the top left of my background", rating: 4 },
    { t: "BTC ticker (top-right)",   say: "add a Bitcoin price ticker to the top right", rating: 4 },
    { t: "ETH ticker (top-right)",   say: "add an Ethereum price ticker to the top right", rating: 3 },
    { t: "Gold ticker (top-right)",  say: "add a Gold price ticker to the top right", rating: 4 },
    { t: "Stock: Tesla",             say: "add a Tesla stock ticker to the top right", rating: 3 },
    { t: "Pin SnapDog dashboard",    say: "pin my snapdog dashboard as a widget on the home screen", rating: 4 },
    { t: "Pin Raw Co dashboard",     say: "pin my raw company hub as a widget on the home screen", rating: 4 },
    { t: "Moon phase",               say: "add a moon phase widget", rating: 2 },
    { t: "All-Seeing Eye",           say: "add the all-seeing eye to the top centre", rating: 3 },
    { t: "Custom text",              say: "add a custom text widget saying FOCUS to the bottom centre", rating: 3 },
    { t: "Clear all widgets",        say: "clear the background", rating: 3 },
    { t: "Open Widgets settings",    say: "open settings on the widgets tab", rating: 5 },
  ]},
  { title: "🎙️ Voice & sound", items: [
    { t: "Voice gallery", say: "show me what voices I can choose from", rating: 5 },
    { t: "Speak slower", say: "speak a bit slower from now on", rating: 4 },
    { t: "Speak faster", say: "speak a bit quicker", rating: 3 },
    { t: "Test Telegram", say: "send a test message to my telegram", rating: 3 },
  ]},
  { title: "🧠 Memory & sessions", items: [
    { t: "Recent chats", say: "show me our recent conversations", rating: 4 },
    { t: "Search chats", say: "search our chats for ", rating: 3 },
    { t: "All my commands", say: "pull up a list of everything I can ask you", rating: 5 },
    { t: "How you work (diagram)", say: "show me a diagram of how you work", rating: 3 },
  ]},
  { title: "🖥️ 2nd screen", items: [
    { t: "Open mirror", say: "send it to my second screen", rating: 3 },
    { t: "Close mirror", say: "close the mirror", rating: 2 },
  ]},
];

export default function CommandRail({ onSend, onReply }) {
  const [collapsed, setCollapsed] = useState(false);
  const [openCats, setOpenCats] = useState(() => CATEGORIES.map((_, i) => i < 5)); // first 5 open
  const [busyKey, setBusyKey] = useState(null);
  const [filter, setFilter] = useState("");

  const toggleCat = (i) => {
    setOpenCats(s => s.map((v, j) => j === i ? !v : v));
  };

  const fire = async (item, key) => {
    setBusyKey(key);
    // Mobile Safari popup-blocker workaround: if this command likely results
    // in a new tab (open_X_app or open_X_site phrases), pre-open a blank
    // window SYNCHRONOUSLY inside the user-gesture, then navigate it once
    // the backend tells us the target URL. Without this, window.open() fires
    // after `await` and Safari blocks it.
    const sayLower = (item.say || "").toLowerCase();
    const looksLikeNewTab = (
      sayLower.includes("open") &&
      !sayLower.includes("dashboard") &&
      !sayLower.includes("hub") &&
      !sayLower.includes("admin") &&
      !sayLower.includes("pin ")
    ) || sayLower.includes("(site)");
    let preopened = null;
    if (looksLikeNewTab) {
      try {
        preopened = window.open("about:blank", "_blank", "noopener,noreferrer");
        // 🛡 iOS Safari Feb 2026: when the ORB tab is closed, any child
        // tab opened via window.open() that still has an opener
        // relationship is auto-killed by Safari (tab-group behaviour).
        // Explicitly severing preopened.opener AND passing noopener in
        // the features string makes the new tab fully independent, so
        // the owner's opened tabs stay alive after closing the orb.
        try { if (preopened) preopened.opener = null; } catch {}
      } catch {}
    }
    try {
      const r = await jarvisChat(item.say);
      // If we got a URL back AND we preopened a window, navigate it now
      const url = r?.action?.result?.url;
      if (preopened && !preopened.closed) {
        if (url) {
          try { preopened.location.href = url; } catch {}
        } else {
          // No URL came back — close the empty tab rather than leave it
          try { preopened.close(); } catch {}
        }
      }
      onSend?.(item.say);
      onReply?.(r);
    } catch (e) {
      console.warn("[CommandRail] send failed", e);
      if (preopened && !preopened.closed) {
        try { preopened.close(); } catch {}
      }
    } finally {
      setTimeout(() => setBusyKey(null), 400);
    }
  };

  const q = filter.trim().toLowerCase();

  return (
    <div
      className={`fixed left-0 top-0 bottom-0 z-[7] flex flex-col transition-all duration-300 ${collapsed ? "w-9" : "w-72"}`}
      data-testid="command-rail"
      style={{
        background: "rgba(0, 8, 22, 0.78)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderRight: "1px solid rgba(212, 175, 55, 0.22)",
        boxShadow: "0 0 24px rgba(212, 175, 55, 0.08)",
      }}
    >
      {/* Header / collapse button */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--rc-gold-dim)]">
        {!collapsed && <div className="rc-label text-[10px] tracking-widest text-[var(--rc-gold)]">// COMMANDS</div>}
        <button
          onClick={() => setCollapsed(c => !c)}
          className="rc-btn !text-[10px] !py-0.5 !px-2"
          data-testid="command-rail-toggle"
          title={collapsed ? "Expand rail" : "Collapse rail"}
        >
          {collapsed ? "▶" : "◀"}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Search */}
          <div className="px-3 py-2 border-b border-[var(--rc-gold-dim)]">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search commands…"
              className="rc-input !text-[11px] !py-1.5"
              data-testid="command-rail-search"
            />
          </div>

          {/* Categories + items */}
          <div className="flex-1 overflow-y-auto py-1">
            {CATEGORIES.map((cat, i) => {
              const matches = q ? cat.items.filter(it => it.t.toLowerCase().includes(q) || it.say.toLowerCase().includes(q)) : cat.items;
              if (q && matches.length === 0) return null;
              const isOpen = q ? true : openCats[i];
              return (
                <div key={i} className="border-b border-[var(--rc-gold-dim)]/40">
                  <button
                    onClick={() => !q && toggleCat(i)}
                    className="w-full flex justify-between items-center px-3 py-1.5 hover:bg-white/5 text-left"
                    data-testid={`cat-toggle-${i}`}
                  >
                    <span className="font-display text-[11px] rc-glow-text">{cat.title}</span>
                    <span className="rc-label text-[9px] opacity-60">{isOpen ? "−" : "+"} {matches.length}</span>
                  </button>
                  {isOpen && (
                    <div className="pb-1">
                      {matches.map((item, j) => {
                        const key = `${i}-${j}`;
                        const busy = busyKey === key;
                        return (
                          <button
                            key={key}
                            onClick={() => fire(item, key)}
                            disabled={busy}
                            className={`w-full text-left px-3 py-1.5 text-[11px] transition-all border-l-2 ${busy ? "border-[var(--rc-gold)] bg-[var(--rc-gold-dim)]/20 rc-glow-text" : "border-transparent hover:border-[var(--rc-gold)] hover:bg-white/5 text-[var(--rc-text)]"}`}
                            data-testid={`cmd-${i}-${j}`}
                            title={`Says: "${item.say}"`}
                          >
                            <div className="flex justify-between items-start gap-2">
                              <span className="flex-1 leading-tight">{item.t}</span>
                              <span className="rc-label text-[8px] opacity-60 shrink-0 tabular-nums" aria-label={`${item.rating} stars`}>
                                {"★".repeat(item.rating)}{"·".repeat(5 - item.rating)}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2 border-t border-[var(--rc-gold-dim)] text-[9px] text-[var(--rc-text-dim)] leading-snug">
            Click any command to fire it. Once your mic arrives, just say the same thing.
          </div>
        </>
      )}
    </div>
  );
}


/* ---------- Helpers ---------- */
const ACKS = [
  "Right away, sir.",
  "Of course, sir — getting that for you now.",
  "On it, sir.",
  "Just a moment, sir.",
  "Coming right up, sir.",
  "Pulling that up for you now, sir.",
  "Right sir — one second.",
  "Yes sir, on it.",
];

// Category-aware preamble — feels less robotic than the same phrase every time.
function buildPreamble(item) {
  const t = (item.t || "").toLowerCase();
  const s = (item.say || "").toLowerCase();
  if (s.includes("close all") || s.includes("close my")) return "Closing that for you now, sir.";
  if (s.includes("buy ") || s.includes("sell ") || s.includes("place a ")) return "Placing the order now, sir.";
  if (s.includes("revenue") || s.includes("calendar") || s.includes("emergent top") || s.includes("month") || s.includes("year"))
    return "Pulling up your numbers, sir.";
  if (s.includes("dashboard") || s.includes("command center") || s.includes("everything"))
    return "Bringing up the command center now, sir.";
  if (s.includes("messages")) return "Fetching your messages, sir.";
  if (s.includes("weather")) return "Checking the weather, sir.";
  if (s.includes("news")) return "Catching up on the headlines now, sir.";
  if (s.includes("background") || s.includes("orb") || s.includes("lightning") || s.includes("widget") || s.includes("eye") || s.includes("ticker") || s.includes("clear"))
    return "Adjusting that for you now, sir.";
  if (s.includes("voices") || s.includes("voice")) return "Opening the voice gallery, sir.";
  if (s.includes("how do you work") || s.includes("diagram")) return "Right sir — pulling up the system map.";
  if (s.includes("everything I can ask") || s.includes("commands")) return "Pulling up your full command list, sir.";
  if (s.includes("recent conversations") || s.includes("chats")) return "Loading the conversation history, sir.";
  if (s.includes("mirror") || s.includes("second screen")) return "Sending it across, sir.";
  if (s.includes("good morning") || s.includes("how's your")) return ""; // let the brain handle these naturally
  if (s === "raw company") return ""; // bare wake word — brain greets
  // Default
  return ACKS[Math.floor(Math.random() * ACKS.length)];
}

// Instant browser TTS — no network round-trip. The full ElevenLabs reply
// follows automatically when the chat response comes back.
function speakInstant(text) {
  if (!text || typeof window === "undefined" || !("speechSynthesis" in window)) return;
  try {
    window.speechSynthesis.cancel(); // stop any previous filler
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 0.92;
    u.volume = 0.95;
    const voices = window.speechSynthesis.getVoices();
    const british = voices.find(v => /en-GB/i.test(v.lang) && /male|daniel|google uk english male/i.test(v.name));
    const anyMale = voices.find(v => /male|daniel|alex/i.test(v.name));
    u.voice = british || anyMale || voices[0];
    window.speechSynthesis.speak(u);
  } catch {}
}
